import * as Sentry from "@sentry/react";

export const SENTRY_REPORTING_STORAGE_KEY = "promang-sentry-reporting-enabled";

/** Sent on API requests when reporting is off so the API skips Sentry for that request. */
export const SENTRY_REPORTING_HEADER = "X-Promang-Sentry-Reporting";

function sentryDsn(): string | undefined {
  return process.env.REACT_APP_SENTRY_DSN;
}

function defaultSentryReportingEnabled(): boolean {
  return process.env.NODE_ENV === "production";
}

export function isSentryConfigured(): boolean {
  return Boolean(sentryDsn());
}

export function isSentryReportingEnabled(): boolean {
  if (!sentryDsn()) return false;
  if (typeof window === "undefined") return defaultSentryReportingEnabled();
  try {
    const stored = window.localStorage.getItem(SENTRY_REPORTING_STORAGE_KEY);
    if (stored !== null) return stored === "true";
  } catch {
    /* ignore */
  }
  return defaultSentryReportingEnabled();
}

const DEFAULT_TRACES_SAMPLE_RATE = 0.1;
/**
 * Share of sessions that keep a rolling replay buffer (uploaded only when an
 * error happens). Buffering records every DOM mutation for the whole session,
 * so it is the main continuous CPU/battery cost of Sentry. Keep it low.
 */
const DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE = 0.1;

function sampleRateFromEnv(value: string | undefined, fallback: number): number {
  if (value === undefined || value === "") return fallback;
  const rate = Number(value);
  if (!Number.isFinite(rate)) return fallback;
  return Math.min(1, Math.max(0, rate));
}

function tracesSampleRate(): number {
  return sampleRateFromEnv(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, DEFAULT_TRACES_SAMPLE_RATE);
}

/** Touch-first devices (phones/tablets) never run replay buffering, to save battery. */
export function isTouchPrimaryDevice(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  try {
    return window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}

export function isSentryReplayEnabled(): boolean {
  return !isTouchPrimaryDevice() && replaysOnErrorSampleRate() > 0;
}

export function replaysOnErrorSampleRate(): number {
  if (isTouchPrimaryDevice()) return 0;
  return sampleRateFromEnv(
    process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    DEFAULT_REPLAYS_ON_ERROR_SAMPLE_RATE
  );
}

/** Browser replay sample rates are not on the generic ClientOptions type. */
type MutableBrowserSentryOptions = {
  enabled?: boolean;
  tracesSampleRate?: number;
  replaysSessionSampleRate?: number;
  replaysOnErrorSampleRate?: number;
};

type ReplayControls = { stop?: () => void; startBuffering?: () => void };

function replayIntegrationOf(client: ReturnType<typeof Sentry.getClient>): ReplayControls | undefined {
  return (client as { getIntegrationByName?: (name: string) => ReplayControls | undefined } | undefined)
    ?.getIntegrationByName?.("Replay");
}

/** True after muting stopped replay; only then may unmuting restart buffering. */
let replayStoppedByMute = false;

function applyClientEnabledState(enabled: boolean): void {
  const client = Sentry.getClient();
  if (!client) return;

  const options = client.getOptions() as MutableBrowserSentryOptions;
  options.enabled = enabled;

  if (!enabled) {
    // Hard-stop sampling so traces/replays cannot flush while muted.
    options.tracesSampleRate = 0;
    options.replaysSessionSampleRate = 0;
    options.replaysOnErrorSampleRate = 0;
    try {
      // Best-effort: stop an in-flight replay buffer if the integration is present.
      const replay = replayIntegrationOf(client);
      if (replay?.stop) {
        replay.stop();
        replayStoppedByMute = true;
      }
    } catch {
      /* ignore */
    }
    return;
  }

  options.tracesSampleRate = tracesSampleRate();
  options.replaysSessionSampleRate = 0;
  options.replaysOnErrorSampleRate = replaysOnErrorSampleRate();

  // The SDK samples replay buffering itself at init. Restart only a buffer that
  // muting stopped, and re-apply the same sampling so unmuting never forces
  // every session to record.
  if (!replayStoppedByMute) return;
  replayStoppedByMute = false;
  if (!isSentryReplayEnabled() || Math.random() >= replaysOnErrorSampleRate()) return;
  try {
    replayIntegrationOf(client)?.startBuffering?.();
  } catch {
    /* ignore */
  }
}

export function setSentryReportingEnabled(enabled: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(SENTRY_REPORTING_STORAGE_KEY, String(enabled));
  } catch {
    /* ignore */
  }
  applyClientEnabledState(enabled);
}

export function initSentry() {
  const dsn = sentryDsn();
  if (!dsn) return;

  Sentry.init({
    dsn,
    enabled: isSentryReportingEnabled(),
    environment: process.env.NODE_ENV,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replays mask all text and block media so customer data never leaves
      // the browser in a recording.
      ...(isSentryReplayEnabled()
        ? [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })]
        : []),
    ],
    tracesSampleRate: tracesSampleRate(),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: replaysOnErrorSampleRate(),
    beforeSend(event) {
      if (!isSentryReportingEnabled()) return null;
      return event;
    },
    beforeSendTransaction(event) {
      if (!isSentryReportingEnabled()) return null;
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (!isSentryReportingEnabled()) return null;
      if (breadcrumb.category === "console") {
        const msg = breadcrumb.message || "";
        if (/password|token|authorization/i.test(msg)) return null;
      }
      return breadcrumb;
    },
  });

  // Ensure sample rates match the persisted preference after init.
  applyClientEnabledState(isSentryReportingEnabled());
}

export function captureSentryException(
  error: unknown,
  captureContext?: Parameters<typeof Sentry.captureException>[1]
) {
  if (!isSentryReportingEnabled()) return;
  Sentry.captureException(error, captureContext);
}

export function addSentryBreadcrumb(breadcrumb: Parameters<typeof Sentry.addBreadcrumb>[0]) {
  if (!isSentryReportingEnabled()) return;
  Sentry.addBreadcrumb(breadcrumb);
}

export function setSentryUserContext(user: { id?: number; email?: string; companyId?: number } | null) {
  if (!sentryDsn() || !isSentryReportingEnabled()) return;
  if (!user?.id) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    company_id: user.companyId ? String(user.companyId) : undefined,
  });
}

export { Sentry };
